import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Segmented, Table, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { DealItem } from '@crm/shared';
import { money, useDeals } from '../features/deals/api';
import { QuickCreateModal } from './QuickCreateModal';
import { EmptyState, tableLoading, tableLocale } from './states';
import { fmtDate } from '../utils/format';

const KIND_COLOR = { OPEN: 'processing', WON: 'success', LOST: 'error' } as const;

/** Deals list for a contact or organization profile. */
export function DealsTab({ contactId, organizationId }: { contactId?: string; organizationId?: string }) {
  const [status, setStatus] = useState<'open' | 'won' | 'lost' | 'all'>('all');
  const [open, setOpen] = useState(false);
  const deals = useDeals({ contactId, organizationId, status, pageSize: 100, sortBy: 'created_at', sortDir: 'desc' });
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
        <Segmented size="small" value={status} onChange={(v) => setStatus(v as typeof status)} options={[{ value: 'all', label: 'All' }, { value: 'open', label: 'Open' }, { value: 'won', label: 'Won' }, { value: 'lost', label: 'Lost' }]} />
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>New deal</Button>
      </div>
      <Table<DealItem>
          rowKey="id"
          size="small"
          loading={tableLoading(deals)}
          scroll={{ x: 'max-content' }}
          locale={tableLocale(
            deals,
            status === 'all' ? (
              <EmptyState description="No deals yet." action={<Button size="small" icon={<PlusOutlined />} onClick={() => setOpen(true)}>New deal</Button>} />
            ) : (
              `No ${status} deals.`
            ),
          )}
          dataSource={deals.data?.items ?? []}
          pagination={false}
          columns={[
            { title: 'Deal', key: 'title', render: (_, d) => <Link to={`/deals/${d.id}`}>{d.title}</Link> },
            { title: 'Stage', key: 'stage', render: (_, d) => <Tag color={d.stage.color ?? KIND_COLOR[d.stage.kind]}>{d.stage.name}</Tag> },
            { title: 'Value', key: 'value', align: 'right', render: (_, d) => money(d.value, d.currency) },
            { title: 'Agent', key: 'agent', render: (_, d) => d.agent?.name ?? '—' },
            { title: 'Close date', key: 'close', render: (_, d) => (d.closedAt ? `Closed ${fmtDate(d.closedAt)}` : d.expectedCloseAt ? `Expected ${fmtDate(d.expectedCloseAt)}` : '—') },
          ]}
        />
      <QuickCreateModal objectType="DEAL" open={open} onClose={() => setOpen(false)} preset={{ contactId, organizationId }} />
    </div>
  );
}
